import { FC, useEffect, useMemo } from "react";

import Stack from "@mui/material/Stack";
import { useForm } from "react-hook-form";

import { useCurrentUserContext } from "@/contexts/CurrentUserContext";
import { DashboardAutocomplete } from "@/dashboard-design-system/Autocomplete/DashboardAutocomplete";
import { DashboardDialog } from "@/dashboard-design-system/Dialog";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { hasLessOrEqualElementsThan, isRequired } from "@/helpers/validators";
import { useTranslate } from "@/hooks/useTranslate";
import { Mail } from "@/icons/outlined";
import { ActiveOrganizationMember } from "@/types/trpc";

interface SendPreviewEmailDialogProps {
  open: boolean;
  onClose: () => void;
  onSend: (previewOrganizationUserIds: string[]) => void;
  activeOrganizationMembers: ActiveOrganizationMember[] | undefined;
  activeOrganizationMembersLoading: boolean;
}

export const SendPreviewEmailDialog: FC<SendPreviewEmailDialogProps> = ({
  open,
  onClose,
  onSend,
  activeOrganizationMembers,
  activeOrganizationMembersLoading,
}) => {
  const { t } = useTranslate();
  const { currentUser, isSendingEmailForbidden } = useCurrentUserContext();

  const defaultRecipient = currentUser?.id ? [{ label: currentUser?.email || "", value: currentUser?.id || "" }] : [];

  const form = useForm<{ recipients: { label: string; value: string }[] }>({
    defaultValues: {
      recipients: defaultRecipient,
    },
    mode: "onChange",
  });
  const { errors } = form.formState;

  const handleSubmit = form.handleSubmit(async ({ recipients }) => {
    if (isSendingEmailForbidden) {
      return;
    }
    await onSend(recipients.map((recipient) => recipient.value));
    onClose();
  });

  useEffect(() => {
    if (open) {
      form.reset({
        recipients: defaultRecipient,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const options = useMemo(() => {
    return (
      activeOrganizationMembers?.map((member) => ({
        label: member.email || "",
        value: member.id,
      })) || []
    );
  }, [activeOrganizationMembers]);

  const { isValid: isFormValid } = form.formState;

  return (
    <>
      <DashboardDialog
        icon={<Mail />}
        open={open}
        onClose={onClose}
        slots={{
          title: (
            <Stack direction="column" gap={0.5}>
              <DashboardTypography variant="h6" vibe="text-neutral-intense">
                {t("dashboard", "emailEditor.sendTest.title")}
              </DashboardTypography>
              <DashboardTypography variant="body2" vibe="text-neutral-quiet">
                {t("dashboard", "emailEditor.sendTest.subtitle")}
              </DashboardTypography>
            </Stack>
          ),
        }}
        actions={{
          main: {
            children: t("dashboard", "common.send"),
            onClick: handleSubmit,
            disabled: isSendingEmailForbidden || activeOrganizationMembersLoading || !isFormValid,
          },
          cancel: {
            onClick: onClose,
          },
        }}
        maxWidth="xs"
        fullWidth
      >
        <DashboardAutocomplete
          name="recipients"
          label={t("dashboard", "signup.emailAddress")}
          control={form.control}
          errorMessage={errors["recipients"]?.message}
          rules={[
            isRequired(t("dashboard", "recipientRequired")),
            hasLessOrEqualElementsThan(10, t("dashboard", "emailEditor.sendTest.tooManyRecipients")),
          ]}
          placeholder={t("dashboard", "signup.emailPlaceholder")}
          disabled={isSendingEmailForbidden || activeOrganizationMembersLoading}
          options={options}
          multiple
          checkbox
        />
      </DashboardDialog>
    </>
  );
};
